import React, { useEffect } from 'react';
import { crmAuthedFetch } from './utils/crmAuthedFetch';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useGlobalProjectHubHotkey } from './hooks/useProjectHubViewModeHotkeys';
import { AuthProvider, useAuth } from './utils/AuthContext';
import { CRMProvider } from './utils/CRMContext';
import { CrmThemeProvider } from './utils/CrmThemeContext';
import { initializeSettings } from './utils/appSettings';
import { watchNotificationEmbedFocus } from './utils/notificationEmbedFocus';
import './utils/fixMessageTypes'; // Load message type fix utility
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Project from './pages/Project';
import ClientManagement from './pages/ClientManagement';
import MapDashboard from './pages/MapDashboard';
import LeadsHub from './pages/LeadsHub';
import LeadForm from './pages/LeadForm';
import { MetaInsightsPage } from './pages/MetaInsightsPage';
import ReportsPage from './pages/ReportsPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectHub from './pages/ProjectHub';
import CommentPopoutPreviewPage from './pages/CommentPopoutPreviewPage';
import ProjectTimelineDisplayPage from './pages/ProjectTimelineDisplayPage';
import PortalTrailPreviewPage from './pages/PortalTrailPreviewPage';
import ProductionSchedulePage from './pages/ProductionSchedulePage';
import ShopBigScreenPage from './pages/ShopBigScreenPage';
import ArchitectPortal from './pages/ArchitectPortal';
import ArchitectStandupPage from './pages/ArchitectStandupPage';
import TeamManagement from './pages/TeamManagement';
import ClientPortalPage from './pages/ClientPortalPage';
import ImportLeads from './pages/ImportLeads';
import ImportPipedrive from './pages/ImportPipedrive';
import ImportMetaAds from './pages/ImportMetaAds';
import LeadTools from './pages/LeadTools';
import ToolsPage from './pages/ToolsPage';
import MediaManagementPage from './pages/MediaManagementPage';
import ExecutiveAssistantCalendarPage from './pages/ExecutiveAssistantCalendarPage';
import UTMStrategyBuilder from './pages/UTMStrategyBuilder';
import CalendarPage from './pages/CalendarPage';
import ExecutivePanel from './pages/ExecutivePanel';
import AgentHarnessPage from './pages/AgentHarnessPage';
import AgentActivityPage from './pages/AgentActivityPage';
import { canViewAgentActivity } from './utils/agentActivityAccess';
import { buildDesignerArchitectDiscussionHubPath } from './utils/architectPortalDiscussionDeepLink';
import {
  canAccessProjectHub,
  hasSalesCrmAccess,
  isArchitectRole,
  isDesignerRole,
  isFullCrmEmployeeAccess,
  isInternal1099ContractorUser,
  isScopedInternalEmployeeRole,
  isSubcontractorRole,
} from './utils/userRoles';
import InviteAcceptPage from './pages/InviteAcceptPage';
import ExternalArchitectPortal from './pages/ExternalArchitectPortal';
import MessagesPage from './pages/MessagesPage';
import TeamChatPage from './pages/TeamChatPage';
import GmailCallback from './pages/GmailCallback';
import EngagementPage from './pages/EngagementPage';
import SalesFlowPage from './pages/SalesFlowPage';
import ProposalBuilderPage from './pages/ProposalBuilderPage';
import ProposalsSummaryPage from './pages/ProposalsSummaryPage';
import WonDealsPage from './pages/WonDealsPage';
import SalesActivityPage from './pages/SalesActivityPage';
import DebitCardSettingsPage from './pages/DebitCardSettingsPage';
import CardExpensesPage from './pages/CardExpensesPage';
import PeopleHubLayout from './pages/PeopleHubLayout';
import PeopleHubPage from './pages/PeopleHubPage';
import PeopleBenefitsPage from './pages/PeopleBenefitsPage';
import PeopleHandbookIndexPage from './pages/PeopleHandbookIndexPage';
import PeopleHandbookPolicyPage from './pages/PeopleHandbookPolicyPage';
import PeopleHandbookTrainingPage from './pages/PeopleHandbookTrainingPage';
import PeopleTravelPage from './pages/PeopleTravelPage';
import PeopleTravelTripPage from './pages/PeopleTravelTripPage';
import PeopleAchievementsPage from './pages/PeopleAchievementsPage';
import PeopleHiringPage from './pages/PeopleHiringPage';
import PeopleOrgChartPage from './pages/PeopleOrgChartPage';
import PeopleTeamPage from './pages/PeopleTeamPage';
import PurchasingPage from './pages/PurchasingPage';
import SettingsLayout from './pages/settings/SettingsLayout';
import AccountSettingsSection from './components/settings/sections/AccountSettingsSection';
import IntegrationsSettingsSection from './components/settings/sections/IntegrationsSettingsSection';
import ProposalsSettingsSection from './components/settings/sections/ProposalsSettingsSection';
import ClientExperienceSettingsSection from './components/settings/sections/ClientExperienceSettingsSection';
import ProjectDefaultsSettingsSection from './components/settings/sections/ProjectDefaultsSettingsSection';
import TeamAccessMapSection from './components/settings/sections/TeamAccessMapSection';
import CrmSettingsSection from './components/settings/sections/CrmSettingsSection';
import ToolsSettingsSection from './components/settings/sections/ToolsSettingsSection';
import ApiSettingsSection from './components/settings/sections/ApiSettingsSection';
import AgentApiKeysSettingsSection from './components/settings/sections/AgentApiKeysSettingsSection';
import ApiUsageSettingsSection from './components/settings/sections/ApiUsageSettingsSection';
import AdminSettingsSection from './components/settings/sections/AdminSettingsSection';

import PublicProposalPage from './pages/PublicProposalPage';
import PublicJobOfferPage from './pages/PublicJobOfferPage';
import PublicCrewOnboardPage from './pages/PublicCrewOnboardPage';
import TradeBriefingPage from './pages/TradeBriefingPage';
import TradeBriefingInboxPage from './pages/TradeBriefingInboxPage';
import PublicChangeOrderPage from './pages/PublicChangeOrderPage';
import ClientPortalSplash from './pages/ClientPortalSplash';
import DesignQuestionnairePage from './pages/DesignQuestionnairePage';
import QuestionnaireLandingPage from './pages/QuestionnaireLandingPage';
import StructuralStylesPage from './pages/StructuralStylesPage';
import JourneyPage from './pages/JourneyPage';
import JourneyManagement from './pages/JourneyManagement';
import ClientPortalManagement from './pages/ClientPortalManagement';
import LeadDocumentUploadBanner from './components/leads/LeadDocumentUploadBanner';
import InspirationBoardPage from './pages/InspirationBoardPage';
import PortalBoardPage from './pages/PortalBoardPage';
import MaterialsDatabasePage from './pages/MaterialsDatabasePage';
import BillingPayablesPage from './pages/BillingPayablesPage';
import InvoiceReviewPage from './pages/InvoiceReviewPage';
import KitManagementPage from './pages/KitManagementPage';
import KitBuilderPage from './pages/KitBuilderPage';
import ProposalDownloadHistoryPage from './pages/ProposalDownloadHistoryPage';
import NotificationsPage from './pages/NotificationsPage';
import DesignTracker from './pages/DesignTracker';
import ShopManagementPage from './pages/ShopManagementPage';
import OperationsHub from './pages/OperationsHub';
import ExternalPortalsPage from './pages/ExternalPortalsPage';
import CrmArchitectureMapPage from './pages/CrmArchitectureMapPage';
import MarketingHubLayout from './pages/MarketingHubLayout';
import MarketingHubLander from './pages/MarketingHubLander';
import MarketingContentPipelinePage from './pages/MarketingContentPipelinePage';
import MarketingRequestsPage from './pages/MarketingRequestsPage';
import MarketingCompetitorBriefingPage from './components/marketing/MarketingCompetitorBriefingPage';
import MarketingOpportunityRadarPage from './components/marketing/MarketingOpportunityRadarPage';
import DxfNestingPage from './pages/DxfNestingPage';
import VendorPortalPage from './pages/VendorPortalPage';
import VendorPortalPreviewPage from './pages/VendorPortalPreviewPage';
import VendorAdminPage from './pages/VendorAdminPage';
import SupplierRfqPortalPage from './pages/SupplierRfqPortalPage';
import ShedCostCalculator from './pages/ShedCostCalculator';
import PublicCrewJobsPage from './pages/PublicCrewJobsPage';
import HandCutVsCncCalculator from './pages/HandCutVsCncCalculator';
import TimberSizingCalculator from './pages/TimberSizingCalculator';
import LifetimeGoalsCalculator from './pages/LifetimeGoalsCalculator';
import SharedFinancialPlanPage from './pages/SharedFinancialPlanPage';
import FinancesHub from './pages/FinancesHub';
import ContractorTimesheetsPage from './pages/ContractorTimesheetsPage';
import NotificationDebugPage from './pages/NotificationDebugPage';
import LoadingScreen from './components/ui/LoadingScreen';
import { LeadTimeProvider } from './context/LeadTimeContext';
import { ProjectTimeProvider } from './context/ProjectTimeContext';
import { SanitizeFinanceProvider } from './context/SanitizeFinanceContext';
import { LeadTimeTracker } from './components/leads/LeadTimeTracker';
import { ProjectTimeTracker } from './components/pm/ProjectTimeTracker';
import CrmActivityTracker from './components/crm/CrmActivityTracker';
import ReleaseAnnouncementGate from './components/release/ReleaseAnnouncementGate';
import EmployeeAwardGate from './components/achievements/EmployeeAwardGate';
import SeasonalCelebrationGate from './components/celebrations/SeasonalCelebrationGate';
import ReleasePublishPrompt from './components/release/ReleasePublishPrompt';
import ReleaseNotesSettingsSection from './components/settings/sections/ReleaseNotesSettingsSection';
import AchievementsSettingsSection from './components/settings/sections/AchievementsSettingsSection';
import FeedbackSettingsSection from './components/settings/sections/FeedbackSettingsSection';
import FeedbackProvider from './components/feedback/FeedbackProvider';
import GlobalCrmSearch from './components/search/GlobalCrmSearch';
import { useContractorPayablesAccess } from './hooks/useContractorPayablesAccess';
import { useContractorTimesheetAccess } from './hooks/useContractorTimesheetAccess';
import { canAccessProjectPL } from './utils/projectPLAccess';

// Authenticated routes component
const QuickBooksOAuthHandler: React.FC = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const realmId = params.get('realmId');

    if (code && state === 'quickbooks_oauth') {
      window.history.replaceState({}, document.title, window.location.pathname);

      (async () => {
        try {
          const response = await crmAuthedFetch('https://us-central1-lctf-projects.cloudfunctions.net/exchangeQuickBooksCode', {
            method: 'POST',
            body: JSON.stringify({
              code,
              redirectUri: 'https://clients.lctimberframes.com',
              realmId,
            }),
          });
          const data = await response.json();
          if (data.success) {
            console.log('[QB OAuth] Token exchange successful, realmId:', realmId);
          } else {
            console.error('[QB OAuth] Exchange failed:', data.error);
          }
        } catch (err) {
          console.error('[QB OAuth] Exchange error:', err);
        }
      })();
    }
  }, []);

  return null;
};

/** Deep links (e.g. Slack "View in CRM") and /crm/leads/:id — full client portal, not map side card */
const LeadRedirect: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  return <Navigate to={`/client/${leadId}`} replace />;
};

/** Keep query string when alias routes move onto /vendor-admin (e.g. ?org=). */
const VendorAdminAliasRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/vendor-admin${search}`} replace />;
};

const RedirectPreservingSearch: React.FC<{ to: string }> = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

/**
 * Designers hitting legacy `/architect-portal?project=&taskComment=` (email
 * "Reply in App") should land on Project Hub Design discussion, not lose the
 * query string on a bare `/designer-portal` navigate.
 */
const DesignerArchitectPortalRedirect: React.FC = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const projectId = params.get('project')?.trim();
  const taskComment = params.get('taskComment')?.trim();
  if (projectId) {
    return (
      <Navigate
        to={buildDesignerArchitectDiscussionHubPath({ projectId, commentId: taskComment })}
        replace
      />
    );
  }
  return <Navigate to={`/designer-portal${search}`} replace />;
};

const AuthenticatedRoutes: React.FC = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const { canAccess: canContractorTimesheets } = useContractorTimesheetAccess();
  const { canAccess: canContractorPayables, canFullFinances } = useContractorPayablesAccess();

  useGlobalProjectHubHotkey({
    enabled: isAuthenticated && !loading && canAccessProjectHub(user?.role),
  });

  const location = useLocation();
  const embedFocusSearchRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && user?.role !== 'client') {
      initializeSettings();
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    const embed = new URLSearchParams(location.search).get('notifEmbed') === '1';
    document.body.classList.toggle('notif-embed', embed);
    if (!embed) {
      embedFocusSearchRef.current = null;
      return () => document.body.classList.remove('notif-embed');
    }
    if (!embedFocusSearchRef.current) embedFocusSearchRef.current = location.search;
    const stop = watchNotificationEmbedFocus(embedFocusSearchRef.current);
    return () => {
      stop();
      document.body.classList.remove('notif-embed');
    };
  }, [location.search]);

  if (loading) {
    return <LoadingScreen size="large" />;
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const isDesigner = isDesignerRole(user?.role);
  const isArchitect = isArchitectRole(user?.role);
  const isSubcontractor = isSubcontractorRole(user?.role);
  const isEngineer = user?.role === 'engineer';
  const isSupplier = user?.role === 'supplier';
  const isCpa = user?.role === 'cpa';
  const isAdmin = user?.role === 'admin';
  const isFullEmployee = isFullCrmEmployeeAccess({
    authRole: user?.role,
    designations: user?.designations,
  });
  const is1099Contractor = user?.is1099Contractor === true;
  const isInternal1099 = isInternal1099ContractorUser({
    role: user?.role,
    is1099Contractor: user?.is1099Contractor,
  });
  const isScopedEmployee =
    isScopedInternalEmployeeRole(user?.role) && !isFullEmployee && !is1099Contractor;
  const hasSalesCrm = hasSalesCrmAccess({
    authRole: user?.role,
    designations: user?.designations,
  });
  /** Full CRM employee routes (Leads, Pipeline, etc.) — not shop/office/staff. */
  const isEmployee = isFullEmployee;
  const isExternalRole = isDesigner || isArchitect || isEngineer;
  const userCanAccessProjectPL = canAccessProjectPL({
    authRole: user?.role,
    designations: user?.designations,
  });
  const canFinancesHub = isAdmin || isCpa || canContractorPayables || canFullFinances || userCanAccessProjectPL;
  const canAgentActivity = canViewAgentActivity({
    authRole: user?.role,
    designations: user?.designations,
  });

  const homeRoute = isFullEmployee || (isScopedEmployee && hasSalesCrm) ? (
    <Navigate to="/leads" replace />
  ) : is1099Contractor ? (
    <Navigate to="/contractor-timesheets" replace />
  ) : isScopedEmployee ? (
    <Navigate to="/project-hub" replace />
  ) : isDesigner ? (
    <ArchitectPortal />
  ) : isArchitect ? (
    <ExternalArchitectPortal />
  ) : isEngineer ? (
    <ArchitectPortal />
  ) : isSupplier ? (
    <Navigate to="/vendor-portal" replace />
  ) : isSubcontractor ? (
    <Navigate to="/briefings" replace />
  ) : isCpa ? (
    <Navigate to="/finances" replace />
  ) : (
    <Dashboard />
  );

  return (
    <FeedbackProvider>
    <SanitizeFinanceProvider>
    <GlobalCrmSearch />
    <ProjectTimeProvider>
    <LeadTimeProvider>
    <QuickBooksOAuthHandler />
    <ReleaseAnnouncementGate />
    <EmployeeAwardGate />
    <SeasonalCelebrationGate />
    {isAdmin && <ReleasePublishPrompt />}
    {isAdmin && <ProjectTimeTracker />}
    {(isEmployee || (isScopedEmployee && hasSalesCrm)) && <LeadTimeTracker />}
    <CrmActivityTracker />
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/" element={homeRoute} />
      {isSubcontractor && (
        <>
          <Route path="/briefings" element={<TradeBriefingInboxPage />} />
          <Route path="/briefing/:token" element={<TradeBriefingPage />} />
        </>
      )}

      {/* Designer routes (legacy /architect-portal URLs redirect; preserve query). */}
      {isDesigner && (
        <>
          <Route path="/designer-portal" element={<ArchitectPortal />} />
          <Route path="/designer-portal/standup" element={<ArchitectStandupPage />} />
          <Route path="/architect-portal" element={<DesignerArchitectPortalRedirect />} />
          <Route path="/architect-portal/standup" element={<RedirectPreservingSearch to="/designer-portal/standup" />} />
          <Route path="/project-hub" element={<ProjectHub />} />
          <Route path="/project-hub/:projectId/timeline-display" element={<ProjectTimelineDisplayPage />} />
          <Route path="/project-hub/:projectId" element={<ProjectHub />} />
          <Route path="/shop-schedule" element={<ProductionSchedulePage />} />
          <Route path="/shop-schedule/display" element={<ShopBigScreenPage />} />
          <Route path="/client/:leadId/project/:dealId/board" element={<InspirationBoardPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/team-chat" element={<TeamChatPage />} />
        </>
      )}

      {isArchitect && (
        <>
          <Route path="/architect-portal" element={<ExternalArchitectPortal />} />
          <Route path="/designer-portal" element={<RedirectPreservingSearch to="/architect-portal" />} />
          <Route path="/project-hub" element={<ProjectHub />} />
          <Route path="/project-hub/:projectId" element={<ProjectHub />} />
          <Route path="/client/:leadId/project/:dealId/board" element={<InspirationBoardPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
        </>
      )}

      {/* Engineer routes */}
      {isEngineer && (
        <>
          <Route path="/designer-portal" element={<ArchitectPortal />} />
          <Route path="/engineer-portal" element={<RedirectPreservingSearch to="/designer-portal" />} />
          <Route path="/architect-portal" element={<DesignerArchitectPortalRedirect />} />
          <Route path="/project-hub" element={<ProjectHub />} />
          <Route path="/project-hub/:projectId/timeline-display" element={<ProjectTimelineDisplayPage />} />
          <Route path="/project-hub/:projectId" element={<ProjectHub />} />
          <Route path="/shop-schedule" element={<ProductionSchedulePage />} />
          <Route path="/shop-schedule/display" element={<ShopBigScreenPage />} />
          <Route path="/client/:leadId/project/:dealId/board" element={<InspirationBoardPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/team-chat" element={<TeamChatPage />} />
        </>
      )}

      {/* Supplier vendor portal */}
      {isSupplier && (
        <>
          <Route path="/vendor-portal" element={<VendorPortalPage />} />
          <Route path="/my-rfqs" element={<SupplierRfqPortalPage />} />
          <Route path="/project-hub" element={<ProjectHub />} />
          <Route path="/project-hub/:projectId" element={<ProjectHub />} />
          <Route path="/shop-schedule" element={<Navigate to="/project-hub" replace />} />
        </>
      )}

      {/* 1099 contractors — timesheets + handbook only */}
      {isInternal1099 && (
        <>
          <Route path="/people" element={<PeopleHubLayout />}>
            <Route index element={<Navigate to="/people/handbook" replace />} />
            <Route path="handbook" element={<PeopleHandbookIndexPage />} />
            <Route path="handbook/training" element={<PeopleHandbookTrainingPage />} />
            <Route path="handbook/training/:nodeId" element={<PeopleHandbookTrainingPage />} />
            <Route path="handbook/:policyId" element={<PeopleHandbookPolicyPage />} />
          </Route>
          <Route path="/notifications" element={<NotificationsPage />} />
        </>
      )}

      {/* Scoped internal employees (field staff, shop, office) */}
      {isScopedEmployee && (
        <>
          <Route path="/project-hub" element={<ProjectHub />} />
          <Route path="/project-hub/:projectId/timeline-display" element={<ProjectTimelineDisplayPage />} />
          <Route path="/project-hub/:projectId" element={<ProjectHub />} />
          <Route path="/shop-schedule" element={<ProductionSchedulePage />} />
          <Route path="/shop-schedule/display" element={<ShopBigScreenPage />} />
          <Route path="/operations" element={<OperationsHub />} />
          <Route path="/purchasing" element={<PurchasingPage />} />
          <Route path="/people" element={<PeopleHubLayout />}>
            <Route index element={<PeopleHubPage />} />
            <Route path="team" element={<PeopleTeamPage />} />
            <Route path="handbook" element={<PeopleHandbookIndexPage />} />
            <Route path="handbook/training" element={<PeopleHandbookTrainingPage />} />
            <Route path="handbook/training/:nodeId" element={<PeopleHandbookTrainingPage />} />
            <Route path="handbook/:policyId" element={<PeopleHandbookPolicyPage />} />
            <Route path="achievements" element={<PeopleAchievementsPage />} />
            <Route path="org" element={<PeopleOrgChartPage />} />
            <Route path="hiring" element={<Navigate to="/people" replace />} />
            {hasSalesCrm ? (
              <>
                <Route path="benefits" element={<PeopleBenefitsPage />} />
                <Route path="travel" element={<PeopleTravelPage />} />
                <Route path="travel/:tripId" element={<PeopleTravelTripPage />} />
              </>
            ) : (
              <>
                <Route path="benefits" element={<Navigate to="/people" replace />} />
                <Route path="travel" element={<Navigate to="/people" replace />} />
                <Route path="travel/:tripId" element={<Navigate to="/people" replace />} />
              </>
            )}
          </Route>
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/team-chat" element={<TeamChatPage />} />
          {/*
            CRM #77: company-card holders (shop/office) must reach /card-expenses from
            digests and the header even without Sales CRM. Page ACL + queries still
            limit them to their own charges; catch-all would bounce them to Project Hub.
          */}
          <Route path="/card-expenses" element={<CardExpensesPage />} />
          {hasSalesCrm && (
            <>
              <Route path="/list" element={<Navigate to="/leads" replace />} />
              <Route path="/leads" element={<LeadsHub />} />
              <Route path="/leads/new" element={<LeadForm />} />
              <Route path="/leads/:id/edit" element={<LeadForm />} />
              <Route path="/pipeline" element={<MapDashboard />} />
              <Route path="/client/:leadId" element={<ClientPortalManagement />} />
              <Route path="/client/:leadId/project/:dealId/board" element={<InspirationBoardPage />} />
              <Route path="/proposal-builder/:dealId" element={<ProposalBuilderPage />} />
              <Route path="/proposals" element={<ProposalsSummaryPage />} />
              <Route path="/proposals/download-history" element={<ProposalDownloadHistoryPage />} />
              <Route path="/won-deals" element={<WonDealsPage />} />
              <Route path="/sales-activity" element={<SalesActivityPage />} />
              <Route path="/analytics" element={<Navigate to="/sales-activity" replace />} />
              <Route path="/map" element={<CrmArchitectureMapPage />} />
              <Route path="/materials" element={<MaterialsDatabasePage />} />
              <Route path="/crm/leads/:leadId" element={<LeadRedirect />} />
              <Route path="/lead/:leadId" element={<LeadRedirect />} />
            </>
          )}
        </>
      )}

      {/* Client routes */}
      {!isExternalRole && !isScopedEmployee && !isSupplier && !isCpa && !isInternal1099 && (
        <>
          <Route path="/list" element={<Navigate to="/leads" replace />} />
          <Route path="/leads" element={<LeadsHub />} />
          {/* `/activities` route removed alongside the Activities toggle in the
              MapDashboard. Lead-level activities still surface inside each
              lead's detail panel, so no data is lost. */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/project/:id" element={<Project />} />
          <Route path="/portal/:projectId" element={<ClientPortalPage />} />
        </>
      )}

      {/* Settings hub — all internal roles except suppliers */}
      {!isSupplier && !isArchitect && (
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="account" replace />} />
          <Route path="account" element={<AccountSettingsSection />} />
          <Route path="integrations" element={<IntegrationsSettingsSection />} />
          <Route path="proposals" element={<ProposalsSettingsSection />} />
          <Route path="client-experience" element={<ClientExperienceSettingsSection />} />
          <Route path="project-defaults" element={<ProjectDefaultsSettingsSection />} />
          <Route path="team" element={<Navigate to="/people/team" replace />} />
          <Route path="team/access" element={<TeamAccessMapSection />} />
          <Route path="crm" element={<CrmSettingsSection />} />
          <Route path="tools" element={<ToolsSettingsSection />} />
          <Route path="api" element={<ApiSettingsSection />} />
          <Route path="agent-api-keys" element={<AgentApiKeysSettingsSection />} />
          <Route path="api-usage" element={<ApiUsageSettingsSection />} />
          <Route path="release-notes" element={<ReleaseNotesSettingsSection />} />
          <Route path="achievements" element={<AchievementsSettingsSection />} />
          <Route path="feedback" element={<FeedbackSettingsSection />} />
          <Route path="admin" element={<AdminSettingsSection />} />
        </Route>
      )}

      {/* Tools — internal & client portal; not timber suppliers or CPA portal */}
      {!isSupplier && !isCpa && !isScopedEmployee && !isInternal1099 && (
        <Route path="/tools" element={<ToolsPage />} />
      )}
      {isAdmin && <Route path="/tools/lifetime-goals" element={<LifetimeGoalsCalculator />} />}
      {!isSupplier && !isCpa && !isScopedEmployee && !isInternal1099 && (
        <Route path="/media-management" element={<MediaManagementPage />} />
      )}
      {!isSupplier && !isCpa && !isScopedEmployee && !isInternal1099 && (
        <Route path="/executive-calendar" element={<ExecutiveAssistantCalendarPage />} />
      )}

      {/* Employee routes */}
      {isEmployee && (
        <>
          <Route path="/leads" element={<LeadsHub />} />
          <Route path="/clients" element={<ClientManagement />} />
          <Route path="/leads/new" element={<LeadForm />} />
          <Route path="/leads/:id/edit" element={<LeadForm />} />
          <Route path="/pipeline" element={<MapDashboard />} />
          <Route path="/design-tracker" element={<DesignTracker />} />
          <Route path="/client/:leadId" element={<ClientPortalManagement />} />
          <Route path="/client/:leadId/project/:dealId/board" element={<InspirationBoardPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/executive" element={<ExecutivePanel />} />
          <Route path="/agent-harness" element={<AgentHarnessPage />} />
          {canAgentActivity && <Route path="/agent-activity" element={<AgentActivityPage />} />}
          <Route path="/inbox" element={<Navigate to="/executive" replace />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/team-chat" element={<TeamChatPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/gmail-callback" element={<GmailCallback />} />
          <Route path="/analytics" element={<Navigate to="/sales-activity" replace />} />
          <Route path="/analytics/meta" element={<MetaInsightsPage />} />
          <Route path="/marketing/meta" element={<Navigate to="/analytics/meta" replace />} />
          <Route path="/utm-strategy" element={<UTMStrategyBuilder />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/sales-activity" element={<SalesActivityPage />} />
          <Route path="/project-pl" element={<Navigate to="/finances/project-pl" replace />} />
          <Route path="/project-pl/cards" element={<DebitCardSettingsPage />} />
          <Route path="/card-expenses" element={<CardExpensesPage />} />
          <Route path="/people-hub" element={<Navigate to="/people" replace />} />
          <Route path="/people" element={<PeopleHubLayout />}>
            <Route index element={<PeopleHubPage />} />
            <Route path="benefits" element={<PeopleBenefitsPage />} />
            <Route path="handbook" element={<PeopleHandbookIndexPage />} />
            <Route path="handbook/training" element={<PeopleHandbookTrainingPage />} />
            <Route path="handbook/training/:nodeId" element={<PeopleHandbookTrainingPage />} />
            <Route path="handbook/:policyId" element={<PeopleHandbookPolicyPage />} />
            <Route path="travel" element={<PeopleTravelPage />} />
            <Route path="travel/:tripId" element={<PeopleTravelTripPage />} />
            <Route path="hiring" element={<PeopleHiringPage />} />
            <Route path="team" element={<PeopleTeamPage />} />
            <Route path="achievements" element={<PeopleAchievementsPage />} />
            <Route path="org" element={<PeopleOrgChartPage />} />
          </Route>
          <Route path="/project-hub" element={<ProjectHub />} />
          <Route path="/project-hub/:projectId/timeline-display" element={<ProjectTimelineDisplayPage />} />
          <Route path="/project-hub/:projectId" element={<ProjectHub />} />
          <Route path="/portal-trail-preview/:portalId/:dealId" element={<PortalTrailPreviewPage />} />
          <Route path="/shop-schedule" element={<ProductionSchedulePage />} />
          <Route path="/shop-schedule/display" element={<ShopBigScreenPage />} />
          <Route path="/designer-portal" element={<ArchitectPortal />} />
          <Route path="/designer-portal/standup" element={<ArchitectStandupPage />} />
          <Route path="/architect-portal" element={<ExternalArchitectPortal />} />
          <Route path="/architect-portal/standup" element={<RedirectPreservingSearch to="/designer-portal/standup" />} />
          <Route path="/engineer-portal" element={<RedirectPreservingSearch to="/designer-portal" />} />
          <Route path="/team" element={<TeamManagement />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/materials" element={<MaterialsDatabasePage />} />
          <Route path="/billing-payables" element={<BillingPayablesPage />} />
          <Route path="/invoice-review" element={<InvoiceReviewPage />} />
          <Route path="/purchasing" element={<PurchasingPage />} />
          <Route path="/import" element={<ImportLeads />} />
          <Route path="/import/pipedrive" element={<ImportPipedrive />} />
          <Route path="/import/meta-ads" element={<ImportMetaAds />} />
          <Route path="/import/tools" element={<LeadTools />} />
          <Route path="/tools/hand-cut-vs-cnc" element={<HandCutVsCncCalculator />} />
          <Route path="/tools/timber-sizing" element={<TimberSizingCalculator />} />
          <Route path="/sales-flow" element={<SalesFlowPage />} />
          <Route path="/proposal-builder/:dealId" element={<ProposalBuilderPage />} />
          <Route path="/proposals" element={<ProposalsSummaryPage />} />
          <Route path="/proposals/download-history" element={<ProposalDownloadHistoryPage />} />
          <Route path="/won-deals" element={<WonDealsPage />} />

          <Route path="/kit-management" element={<KitManagementPage />} />
          <Route path="/kit-builder/:kitId?" element={<KitBuilderPage />} />
          <Route path="/shop-management" element={<ShopManagementPage />} />
          <Route path="/operations" element={<OperationsHub />} />
          <Route path="/external-portals" element={<ExternalPortalsPage />} />
          <Route path="/map" element={<CrmArchitectureMapPage />} />
          <Route path="/marketing-hub" element={<MarketingHubLayout />}>
            <Route index element={<MarketingHubLander />} />
            <Route path="content" element={<MarketingContentPipelinePage />} />
            <Route path="content-requests" element={<MarketingRequestsPage />} />
            <Route path="competitor-briefing" element={<MarketingCompetitorBriefingPage />} />
            <Route path="opportunity-radar" element={<MarketingOpportunityRadarPage />} />
          </Route>
          <Route path="/dxf-nesting" element={<DxfNestingPage />} />
          <Route path="/supplier-rfqs" element={<VendorAdminAliasRedirect />} />
          <Route path="/vendor-portal" element={<VendorPortalPage />} />
          <Route path="/vendor-portal/preview" element={<VendorPortalPreviewPage />} />
          <Route path="/vendor-admin" element={<VendorAdminPage />} />
          <Route path="/vendors" element={<VendorAdminAliasRedirect />} />
          <Route path="/journey-management" element={<JourneyManagement />} />
          <Route path="/crm/leads/:leadId" element={<LeadRedirect />} />
          <Route path="/lead/:leadId" element={<LeadRedirect />} />
          {isAdmin && <Route path="/debug/notifications" element={<NotificationDebugPage />} />}
        </>
      )}
      {canContractorTimesheets && (
        <Route path="/contractor-timesheets" element={<ContractorTimesheetsPage />} />
      )}
      {canFinancesHub && <Route path="/finances/*" element={<FinancesHub />} />}
      <Route path="/comment-popout" element={<CommentPopoutPreviewPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </LeadTimeProvider>
    </ProjectTimeProvider>
    </SanitizeFinanceProvider>
    </FeedbackProvider>
  );
};

// Main App Routes - includes public routes
const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Public routes - no authentication required, NO CRMProvider */}
        <Route path="/engagement/:slug" element={<EngagementPage />} />
        <Route path="/proposal/:slug" element={<PublicProposalPage />} />
        <Route path="/offer/:slug" element={<PublicJobOfferPage />} />
        <Route path="/briefing/:token" element={<TradeBriefingPage />} />
        <Route path="/briefings" element={<TradeBriefingInboxPage />} />
        <Route path="/change-order/:slug" element={<PublicChangeOrderPage />} />
        <Route path="/portal/:slug" element={<ClientPortalSplash />} />
        <Route path="/portal/:slug/board" element={<PortalBoardPage />} />
        <Route path="/portal/:slug/questionnaire" element={<DesignQuestionnairePage />} />
        <Route path="/questionnaire" element={<QuestionnaireLandingPage />} />
        <Route path="/structural-styles" element={<StructuralStylesPage />} />
        <Route path="/journey" element={<JourneyPage />} />
        <Route path="/plan-share/:slug" element={<SharedFinancialPlanPage />} />

        {/* Hidden personal tool — unlinked from any nav, no auth required */}
        <Route path="/shed-calc" element={<ShedCostCalculator />} />
        <Route path="/crew-jobs" element={<PublicCrewJobsPage />} />
        <Route path="/crew-jobs/:slug" element={<PublicCrewJobsPage />} />
        <Route path="/crew-onboard/:slug" element={<PublicCrewOnboardPage />} />
        <Route path="/invite/:inviteId" element={<InviteAcceptPage />} />

        {/* All other routes require authentication AND CRM context */}
        <Route path="/*" element={
          <CRMProvider>
            <CrmThemeProvider>
              <AuthenticatedRoutes />
            </CrmThemeProvider>
          </CRMProvider>
        } />
      </Routes>
      <LeadDocumentUploadBanner />
    </Router>
  );
};

const App: React.FC = () => {

  return (
    <AuthProvider>
      <div className="App">
        <AppRoutes />
      </div>
    </AuthProvider>
  );
};

export default App;
